import React, {
    forwardRef,
    ReactNode,
    useEffect,
    useImperativeHandle,
    useRef,
    useState,
} from "react";
import {
    TextField,
    FormControl,
    Stack,
    Button,
    Checkbox,
    useTheme,
    useMediaQuery,
    Box,
    Tabs,
    Tab,
    Autocomplete,
    Pagination,
    paginationClasses,
    CircularProgress,
    Divider,
    Typography,
    Popover,
} from "@mui/material";
// import { DateRangePicker } from "@mui/x-date-pickers-pro/DateRangePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import Iconify from "../iconify";
import Label from "../label";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../../store";
import FilterConfig, { Filter, SearchTypes, Types } from "./FilterConfig";
import {
    setTableLoading,
    updateFilter,
    updateTableData,
    updateTableExtras,
    updateTotalRecords,
} from "../../features/table/tableStore";
import { STATUS_COUNT_GET, TABLE_FILTER_GET } from "../../util/newconfig";
import advService from "../../util/advService";
import { AxiosResponse } from "axios";
import { JavaResponse } from "../../types";
import Dropdown from "../Input/Dropdown";
import { Input } from "antd";
import { BASE_URL } from "../../util/config";
import { useLocation, useNavigate } from "react-router-dom";
import evaluate from "../../util/function/evaluate";
import CustomDateRangePicker from "../CustomDateRangePicker";
import FilterMultiSelect from "./MultiSelectTabs";
import dayjs from "dayjs";
import "./FilterMaker.scss";

interface FilterMakerRef {
    setPage: (page: number) => void;
    setTabValue: (tabValue: string) => void;
    tab: string;
}

const Search =
    "https://imtbepublic.s3.ap-south-1.amazonaws.com/oldAssets/assets/images/icons/search.svg";

const FilterMaker = forwardRef<
    FilterMakerRef,
    {
        config: FilterConfig;
        refreshKey?: number;
        lastProcessedDate?: string;
        children: ReactNode;
    }
>(({ config, refreshKey, lastProcessedDate, children }, ref) => {
    const dispatch = useDispatch();

    const loading = useSelector(
        (state: RootState) => state.table["default"]?.loading
    );
    const totalRecords = useSelector(
        (state: RootState) => state.table["default"]?.totalRecords
    );

    const selectedRows = useSelector(
        (state: RootState) => state.table["default"]?.selectedRows
    );

    const anchorRef = useRef<HTMLImageElement>(null);
    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const handleClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };
    const handleClose = () => {
        setAnchorEl(null);
    };

    const [tabValue, setTabValue] = useState(
        config.statusOptions ? config.statusOptions[0].value : "all"
    );
    const [filterValues, setFilterValues] = useState<Record<string, any>>({});
    const [page, setPage] = useState(0);
    const [counts, setCounts] = useState<Record<string, number>>({});
    const [dateRange, setDateRange] = useState<[Date | null, Date | null]>([
        null,
        null,
    ]);
    const location = useLocation();

    const getQueryParam = (key: string): string | null => {
        const params = new URLSearchParams(location.search);
        return params.get(key);
    };

    const navigate = useNavigate();
    const [orderBy, setOrderBy] = useState<string | null>(getQueryParam("ob"));
    const [order, setOrder] = useState<number | null>(
        getQueryParam("o") && !isNaN(parseInt(getQueryParam("o")!, 10))
            ? parseInt(getQueryParam("o")!, 10)
            : null
    );
    const [searchTerm, setSearchTerm] = useState<string>("");
    const [selectedColumn, setSelectedColumn] = useState<string | null>(
        config.filters.find((f) => f.type === Types.search)?.options?.[0].value
    );

    const [searchFilter, setSearchFilter] = useState<{
        label: string;
        value: string;
    } | null>(null);

    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    useImperativeHandle(ref, () => ({
        setPage,
        setTabValue,
        tab: tabValue,
    }));

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        const o = params.get("o");
        const parsedOrder =
            o && !isNaN(parseInt(o, 10)) ? parseInt(o, 10) : null;

        if (parsedOrder !== null && parsedOrder !== order) {
            setOrder(parsedOrder);
        }

        const ob = params.get("ob");
        if (ob && ob !== orderBy) {
            setOrderBy(ob);
        }
    }, [location.search]);

    useEffect(() => {
        const params = new URLSearchParams(location.search);

        const tabParam = params.get("tab");
        if (tabParam && tabParam !== tabValue) {
            setTabValue(tabParam);
        }

        const pageParam = params.get("page");
        const parsedPage = pageParam ? parseInt(pageParam, 10) : 0;
        if (parsedPage !== page) {
            setPage(parsedPage);
        }

        const orderParam = params.get("o");
        const parsedOrder = orderParam ? parseInt(orderParam, 10) : null;
        if (parsedOrder !== order) {
            setOrder(parsedOrder);
        }

        const orderByParam = params.get("ob");
        if (orderByParam !== orderBy) {
            setOrderBy(orderByParam);
        }

        const searchParam = params.get("search");
        if (searchParam) {
            try {
                const parsed = JSON.parse(searchParam);
                const newSearchTerm = parsed.v || "";
                const newSelectedColumn = parsed.c || null;

                if (
                    newSearchTerm !== searchTerm ||
                    newSelectedColumn !== selectedColumn
                ) {
                    setSearchTerm(newSearchTerm);
                    setSelectedColumn(newSelectedColumn);
                    setSearchFilter({
                        label: newSelectedColumn,
                        value: newSearchTerm,
                    });
                }
            } catch (error) {
                console.error("Error parsing search parameter:", error);
            }
        }

        const newFilterValues: Record<string, any> = {};
        let hasChanges = false;

        config.filters.forEach((filter) => {
            const paramValue = params.get(filter.label);
            if (paramValue) {
                try {
                    const parsed = JSON.parse(paramValue);
                    newFilterValues[filter.label] = parsed.v;

                    if (
                        JSON.stringify(filterValues[filter.label]) !==
                        JSON.stringify(parsed.v)
                    ) {
                        hasChanges = true;
                    }

                    if (
                        filter.type === "dateDropdown" &&
                        Array.isArray(parsed.v) &&
                        (!dateRange[0] ||
                            !dateRange[1] ||
                            dateRange[0].getTime() !== parsed.v[0] ||
                            dateRange[1].getTime() !== parsed.v[1])
                    ) {
                        setDateRange([
                            new Date(parsed.v[0]),
                            new Date(parsed.v[1]),
                        ]);
                    }
                } catch (error) {
                    console.error(
                        `Error parsing filter ${filter.label}:`,
                        error
                    );
                }
            }
        });
        if (
            hasChanges ||
            Object.keys(newFilterValues).length !==
                Object.keys(filterValues).length
        ) {
            setFilterValues(newFilterValues);
        }
    }, []);

    useEffect(() => {
        if (config.url) {
            updateUrl();
            fetchData();
        }

        if (config.statusCountUrl) {
            fetchStatusCounts();
        }
    }, [
        filterValues,
        tabValue,
        page,
        order,
        orderBy,
        searchFilter,
        config.url,
        config.table,
        refreshKey,
    ]);

    const updateUrl = () => {
        const params = new URLSearchParams();

        if (config.statusOptions) {
            params.set("tab", tabValue);
        }

        params.set("page", page.toString());

        if (order !== null) {
            params.set("o", order.toString());
        }

        if (orderBy) {
            params.set("ob", orderBy);
        }

        if (searchFilter?.label && searchFilter?.value) {
            params.set(
                "search",
                JSON.stringify({
                    c: searchFilter.label,
                    v: searchFilter.value,
                    t: SearchTypes.like,
                })
            );
        }

        Object.entries(filterValues).forEach(([key, value]) => {
            const filter = config.filters.find((f) => f.label === key);
            if (
                filter &&
                value !== undefined &&
                value !== null &&
                value !== "" &&
                !(Array.isArray(value) && value.length === 0)
            ) {
                params.set(
                    key,
                    JSON.stringify({
                        c: filter.column,
                        v: value,
                        t: filter.type,
                    })
                );
            }
        });

        const newUrl = `${location.pathname}?${params.toString()}`;
        // window.history.pushState({}, "", newUrl);
        navigate(newUrl, { replace: true });
    };

    const fetchData = async () => {
        dispatch(setTableLoading({ loading: true }));

        try {
            const filters = config.filters
                .map((filter) => {
                    if (filterValues[filter.label] !== undefined) {
                        let value = filterValues[filter.label];

                        if (
                            value === "" ||
                            (Array.isArray(value) && value.length === 0)
                        ) {
                            return null;
                        }

                        return {
                            c: filter.column,
                            v: value,
                            st: filter.searchType || 1,
                        };
                    }
                    return null;
                })
                .filter(Boolean);

            if (searchFilter?.label) {
                filters.push({
                    c: searchFilter.label,
                    v: searchFilter.value,
                    st: 2,
                });
            }

            if (config.statusOptions && tabValue !== "all") {
                const statusOption = config.statusOptions.find(
                    (opt) => opt.value === tabValue
                );
                if (statusOption && config.tabKey) {
                    filters.push({
                        c: config.tabKey,
                        v: statusOption.backendValue,
                        st: 1,
                    });
                }
            }

            const filtersDTO = {
                p: [page, config.pageSize],
                e: config.table,
                ...(order !== -1 && order !== undefined && order !== null
                    ? { o: order }
                    : {}),
                ...(orderBy && order !== -1 ? { ob: orderBy } : {}),
                f: filters,
            };

            dispatch(updateFilter({ filter: filtersDTO }));

            const configObj = {
                ...TABLE_FILTER_GET,
                url: `${BASE_URL}${config.url}/filter?${encodeURIComponent(
                    JSON.stringify(filtersDTO)
                )}`,
            };

            try {
                await advService(
                    configObj,
                    {},
                    (res: AxiosResponse<JavaResponse<any>>) => {
                        if (res?.data?.success) {
                            const responsePath = config.dataPath
                                ? (res.data as any)[config.dataPath]
                                : res.data.response.d_table_response;

                            const data = responsePath?.data || [];
                            const records = responsePath?.records || 0;

                            dispatch(updateTableData({ data }));
                            dispatch(updateTotalRecords({ total: records }));
                            if (res.data.response?.remainingSumInsured) {
                                dispatch(
                                    updateTableExtras({
                                        extras: {
                                            remainingSumInsured:
                                                res.data.response
                                                    .remainingSumInsured,
                                        },
                                    })
                                );
                            } else {
                                dispatch(
                                    updateTableExtras({
                                        extras: {
                                            remainingSumInsured: 0,
                                        },
                                    })
                                );
                            }
                        } else {
                            dispatch(updateTableData({ data: [] }));
                            dispatch(updateTotalRecords({ total: 0 }));
                        }
                    }
                );
            } catch (error) {
                console.error("Error fetching table data:", error);
                dispatch(updateTableData({ data: [] }));
                dispatch(updateTotalRecords({ total: 0 }));
            } finally {
                dispatch(setTableLoading({ loading: false }));
            }
        } catch (error) {
            console.error("Error fetching data:", error);
            dispatch(setTableLoading({ loading: false }));
        }
    };

    const fetchStatusCounts = async () => {
        try {
            const filters: any[] = [];

            const filtersDTO = {
                p: [0, config.pageSize],
                e: config.table,
                f: filters,
            };

            const configObj = {
                ...STATUS_COUNT_GET,
                url: `${BASE_URL}${config.statusCountUrl}?${encodeURIComponent(
                    JSON.stringify(filtersDTO)
                )}`,
            };

            advService(
                configObj,
                {},
                (res: AxiosResponse<JavaResponse<any>>) => {
                    if (res?.data?.success) {
                        const responsePath = config.dataPath
                            ? (res.data as any)[config.dataPath]
                            : res.data.response.d_table_counts;
                        const data = responsePath;
                        setCounts(data);
                    }
                }
            );
        } catch (error) {
            console.error("Error fetching status counts:", error);
        }
    };

    const handleInputChange = (label: string, value: any) => {
        setPage(0);
        if (Array.isArray(value) && value.includes(-1) && label !== "Status") {
            const index = value.indexOf(-1);
            if (index === value.length - 1) {
                value = [-1];
            } else {
                value = value.filter((v: any) => v !== -1);
            }
        }
        setFilterValues((prev) => ({ ...prev, [label]: value }));
    };

    // const clearAllFilters = () => {
    //     setFilterValues({});
    //     setPage(0);
    //     setDateRange([null, null]);
    //     setOrder(null);
    //     setOrderBy(null);
    //     setSearchTerm("");
    // };
    const renderTopSectionButtons = () => {
        return (
            <Stack direction="row" spacing={2} flexWrap={"wrap"}>
                {config.topSectionButtons?.map((button) => {
                    const isAllowed = evaluate(button.condition || "true");

                    if (!isAllowed) return null;

                    return (
                        <button
                            key={button.label}
                            // variant="outlined"
                            onClick={() => {
                                button.func();
                            }}
                            className="buttonPrimary pr20 pl20 mr10"
                            style={{
                                height: "47px",
                            }}
                            // className=""
                            //  startIcon={
                            //      button.icon ? getIcon(button.icon) : undefined
                            //  }
                            // sx={{
                            //     width: "130px",
                            //     color: "white",
                            //     borderColor: button?.color || "#5668d1",
                            //     backgroundColor: button?.color || "#5668d1",
                            //     "&:hover": {
                            //         backgroundColor: button?.color || "#5668d1",
                            //         borderColor: button?.color || "#5668d1",
                            //     },
                            // }}
                        >
                            {button.label}
                        </button>
                    );
                })}
            </Stack>
        );
    };
    const renderSearch = () => {
        const searchFilter = config.filters.find(
            (f) => f.type === Types.search
        );
        if (!searchFilter) return null;

        const handleSearch = () => {
            if (searchTerm && selectedColumn) {
                setPage(0);
                setSearchFilter({
                    label: selectedColumn,
                    value: searchTerm,
                });
            }
        };

        return (
            <div
                className={`search___container pr10 pl10 pt4 pb4 ${
                    JSON.parse(sessionStorage.getItem("user") || "{}")
                        ?.userType === "EMPLOYEE"
                        ? "search___container_employee"
                        : ""
                }`}
            >
                {(searchFilter.options ?? []).length > 0 && (
                    <Dropdown
                        defaultVal={selectedColumn}
                        items={(searchFilter.options ?? []).map((option) => ({
                            id: option.value,
                            name: option.tag,
                            value: option.value,
                        }))}
                        change={(val: string) => {
                            setSelectedColumn(val);
                        }}
                        variant="standard"
                    />
                )}

                <div className="table__searchbar">
                    <Input
                        placeholder="Search"
                        onChange={(e) => {
                            if (e.target.value) {
                                setSearchTerm(e.target.value);
                            }
                            if (e.target.value.length === 0) {
                                setSearchTerm("");
                                setSearchFilter(null);
                            }
                        }}
                        onPressEnter={handleSearch}
                        className="custom-search-input"
                        value={searchTerm}
                    />
                    <Iconify
                        icon="material-symbols:search"
                        onClick={handleSearch}
                        className="search___icon"
                    />
                </div>
            </div>
        );
    };

    const renderPopoverSearch = () => {
        const anchorRect = anchorRef.current
            ? anchorRef.current?.getBoundingClientRect()
            : null;
        const popoverStyle = anchorRect
            ? { top: anchorRect.top, left: anchorRect.left }
            : {};
        return (
            <>
                <img
                    ref={anchorRef}
                    src={Search}
                    onClick={handleClick}
                    className="table___popoverIcon"
                    alt=""
                />

                <Popover
                    open={Boolean(anchorEl)}
                    anchorEl={anchorEl}
                    onClose={handleClose}
                    PaperProps={{
                        style: {
                            position: "fixed",
                            ...popoverStyle,
                        },
                    }}
                >
                    {renderSearch()}
                </Popover>
            </>
        );
    };

    const renderFilter = (filter: Filter, index: number) => {
        if (filter.type === Types.search) return null;

        switch (filter.type) {
            case Types.multiSelectTabs:
                return (
                    <FilterMultiSelect
                        key={index}
                        options={filter.options || []}
                        value={filterValues[filter.label] || []}
                        onChange={(newValue) =>
                            handleInputChange(filter.label, newValue)
                        }
                        maxWidth="500px"
                        multiSelect={true}
                        showCounts={false}
                    />
                );

            case Types.singleSelect:
                return (
                    <FormControl
                        key={index}
                        // size={"small"}
                        sx={{ width: "220px" }}
                    >
                        <Autocomplete
                            options={filter.options || []}
                            getOptionLabel={(option) => option.label}
                            value={
                                filter.options?.find(
                                    (option) =>
                                        option.value ===
                                        filterValues[filter.label]
                                ) || null
                            }
                            onChange={(event, newValue) =>
                                handleInputChange(
                                    filter.label,
                                    newValue ? newValue.value : ""
                                )
                            }
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={filter.label}
                                    variant="outlined"
                                    size={"small"}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            bgcolor: "white",
                                            height: "47px",
                                        },
                                    }}
                                />
                            )}
                        />
                    </FormControl>
                );

            case Types.multiSelect:
                return (
                    <FormControl
                        key={index}
                        // size={"small"}
                        sx={{ width: "220px" }}
                    >
                        <Autocomplete
                            multiple
                            options={filter.options || []}
                            disableCloseOnSelect
                            limitTags={2}
                            renderTags={(value, getTagProps) =>
                                value.map((option, index) => (
                                    <span
                                        {...getTagProps({ index })}
                                        style={{
                                            fontSize: "14px",
                                            margin: 0,
                                        }}
                                    >
                                        {`${option.label}${
                                            index === value.length - 1
                                                ? ""
                                                : ", "
                                        }`}
                                    </span>
                                ))
                            }
                            getOptionLabel={(option) => option.label}
                            value={
                                filter.options?.filter((option) =>
                                    filterValues[filter.label]?.includes(
                                        option.value
                                    )
                                ) || []
                            }
                            onChange={(event, newValue) => {
                                handleInputChange(
                                    filter.label,
                                    newValue.map((item) => item.value)
                                );
                            }}
                            renderOption={(props, option, { selected }) => (
                                <li {...props}>
                                    <Checkbox checked={selected} />
                                    {option.label}
                                </li>
                            )}
                            renderInput={(params) => (
                                <TextField
                                    {...params}
                                    label={filter.label}
                                    variant="outlined"
                                    size={"small"}
                                    sx={{
                                        "& .MuiOutlinedInput-root": {
                                            bgcolor: "white",
                                            height: "47px",
                                        },
                                    }}
                                />
                            )}
                        />
                    </FormControl>
                );

            case Types.dateDropdown:
                return (
                    <CustomDateRangePicker
                        key={index}
                        value={dateRange}
                        onChange={(newValue) => {
                            setDateRange(newValue);
                            if (newValue[0] && newValue[1]) {
                                handleInputChange(filter.label, [
                                    newValue[0].getTime(),
                                    newValue[1].getTime(),
                                ]);
                            }
                            if (!newValue[0] && !newValue[1]) {
                                setFilterValues((prev) => {
                                    const updated = { ...prev };
                                    delete updated[filter.label];
                                    return updated;
                                });
                            }
                        }}
                        label={filter.label}
                        isMobile={isMobile}
                    />
                );

            default:
                return null;
        }
    };

    const renderTabs = () => {
        if (!config.statusOptions) return null;

        const activeTabIndex = config.statusOptions.findIndex(
            (tab) => tab.value === tabValue
        );

        return (
            <Tabs
                value={tabValue}
                onChange={(e, newValue) => {
                    setPage(0);
                    setTabValue(newValue);
                }}
                textColor={undefined}
                TabIndicatorProps={{
                    style: {
                        backgroundColor:
                            tabValue === "all"
                                ? theme.palette.primary.main
                                : config.statusOptions[activeTabIndex]?.color,
                    },
                }}
                variant="scrollable"
                scrollButtons="auto"
                style={{
                    // marginBottom: "0.5rem",
                    // marginRight: "0.5rem",
                    backgroundColor: "white",
                    borderRadius: "6px",
                    width: "fit-content",
                    // height: "",
                }}
            >
                {/* <Tab
                    value="all"
                    label={
                        <>
                            All (
                            {Object.values(counts).reduce(
                                (sum, count) => sum + count,
                                0
                            )}
                            )
                        </>
                    }
                    style={{ minWidth: 0 }}
                /> */}
                {config.statusOptions.map((tab) => (
                    <Tab
                        key={tab.value}
                        value={tab.value}
                        label={
                            <>
                                {tab.label} ({counts[tab.value] || 0})
                            </>
                        }
                        style={{
                            minWidth: 0,
                            color:
                                tab.value === tabValue ? tab.color : undefined,
                        }}
                    />
                ))}
            </Tabs>
        );
    };

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                width: "100%",
                gap: 2,
            }}
            className="table-top-row-filter"
        >
            {!selectedRows.some((row) => row.data.length > 0) && (
                <Stack
                    gap={2}
                    direction="row"
                    alignItems="center"
                    justifyContent={"space-between"}
                    flexWrap="wrap"
                    // sx={{ p: 2 }}
                >
                    {window.innerWidth <= 1150
                        ? renderPopoverSearch()
                        : renderSearch()}
                    <>
                        {config.filters.length > 1 && (
                            <>
                                {/* <Divider
                                    orientation="vertical"
                                    flexItem
                                    sx={{
                                        display: { xs: "none", sm: "block" },
                                    }}
                                /> */}
                                <Stack
                                    flexDirection={"row"}
                                    gap={"16px"}
                                    flexWrap={"wrap"}
                                    sx={{
                                        marginLeft: "auto",
                                    }}
                                    className="table__filter"
                                >
                                    {config.filters
                                        .filter((f) => f.type !== Types.search)
                                        .map(renderFilter)}
                                </Stack>
                            </>
                        )}
                    </>
                    {config.statusOptions && (
                        <>
                            {/* <Divider
                                orientation="vertical"
                                flexItem
                                sx={{
                                    display: { xs: "none", sm: "block" },
                                }}
                            /> */}
                            {renderTabs()}
                        </>
                    )}

                    {(config?.topSectionButtons ?? []).length > 0 && (
                        <>
                            {/* <Divider
                                orientation="vertical"
                                flexItem
                                sx={{
                                    display: { xs: "none", sm: "block" },
                                }}
                            /> */}

                            {renderTopSectionButtons()}
                        </>
                    )}
                    {!!lastProcessedDate ? (
                        <div
                            className="table-top-row_right"
                            style={{
                                flexWrap: "wrap",
                                alignItems: "center",
                                justifySelf: "flex-end",
                                // marginLeft: "auto",
                            }}
                        >
                            <Typography variant="caption" color={"black"}>
                                Last refreshed on{" "}
                                {/* {changeDateFormat(
                                                                "javascriptDateObject",
                                                                "dd-mmm-yyyy",
                                                                new Date(lastProcessedDate)
                                                            )} */}
                                {dayjs(lastProcessedDate).format(
                                    "DD-MMM-YYYY hh:mm:ss A"
                                )}
                            </Typography>
                        </div>
                    ) : null}
                </Stack>
            )}

            {/* {loading ? (
                <Box display="flex" justifyContent="center" p={4}>
                    <CircularProgress />
                </Box>
            ) : ( */}
            <Box
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    minHeight: "100%",
                }}
            >
                <Box sx={{ flexGrow: 1 }}>{children}</Box>
                {(totalRecords ?? 0) > 0 && (
                    <Pagination
                        count={Math.ceil(
                            (totalRecords ?? 0) / (config.pageSize ?? 1)
                        )}
                        page={page + 1}
                        onChange={(_, value) => setPage(value - 1)}
                        className="custom-pagination"
                        sx={{
                            mt: 3,
                            [`& .${paginationClasses.ul}`]: {
                                justifyContent: "center",
                            },
                            position: "static",
                        }}
                    />
                )}
            </Box>
            {/* )} */}
        </Box>
    );
});

export default FilterMaker;
